import React, { useMemo, useState } from 'react';
import { PicklistOption, FieldMapping, DataType } from '../../types/metadata';
import { PicklistOptionModal } from './PicklistOptionModal';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { EmptyState } from '../shared/EmptyState';
import { Edit, Trash2, ChevronUp, ChevronDown, Plus, List, Zap, X } from 'lucide-react';
interface PicklistOptionListProps {
  options: PicklistOption[];
  fields: FieldMapping[];
  onAdd: (option: Omit<PicklistOption, 'id'>) => void;
  onBulkAdd: (options: Omit<PicklistOption, 'id'>[]) => void;
  onUpdate: (id: string, updates: Partial<PicklistOption>) => void;
  onDelete: (id: string) => void;
  onReorder: (id: string, direction: 'up' | 'down') => void;
}
export function PicklistOptionList({
  options,
  fields,
  onAdd,
  onBulkAdd,
  onUpdate,
  onDelete,
  onReorder
}: PicklistOptionListProps) {
  const picklistFields = useMemo(() => fields.filter(f => f.dataType === DataType.Picklist), [fields]);
  const [selectedQuestionKey, setSelectedQuestionKey] = useState<string>(picklistFields[0]?.questionKey || '');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [editingOption, setEditingOption] = useState<PicklistOption | undefined>();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const filteredOptions = useMemo(() => {
    return options.filter(o => o.questionKey === selectedQuestionKey).sort((a, b) => a.order - b.order);
  }, [options, selectedQuestionKey]);
  const selectedField = picklistFields.find(f => f.questionKey === selectedQuestionKey);
  const handleEdit = (option: PicklistOption) => {
    setEditingOption(option);
    setIsModalOpen(true);
  };
  const handleSave = (data: any) => {
    if (editingOption) {
      onUpdate(editingOption.id, data);
    } else {
      onAdd(data);
    }
  };
  const handleBulkSave = () => {
    if (!bulkText.trim() || !selectedQuestionKey) return;
    const lines = bulkText.split('\n').filter(line => line.trim());
    const newOptions = lines.map((line, index) => {
      const parts = line.split('|');
      const label = parts[0].trim();
      const value = parts.length > 1 ? parts[1].trim() : label.replace(/\s+/g, '');
      return {
        questionKey: selectedQuestionKey,
        optionLabel: label,
        optionValue: value,
        order: filteredOptions.length + index + 1,
        active: true
      };
    });
    onBulkAdd(newOptions);
    setIsBulkModalOpen(false);
    setBulkText('');
  };
  return <div className="space-y-4" data-id="element-172">
      {/* Selector */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200" data-id="element-173">
        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-174">
          Select Picklist Field to Manage
        </label>
        <select value={selectedQuestionKey} onChange={e => setSelectedQuestionKey(e.target.value)} className="block w-full max-w-2xl px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-175">
          {picklistFields.length === 0 && <option disabled value="" data-id="element-176">
              No picklist fields available
            </option>}
          {picklistFields.map(field => <option key={field.id} value={field.questionKey} data-id="element-177">
              {field.promptText} ({field.questionKey})
            </option>)}
        </select>

        {selectedField && <div className="mt-3 flex gap-4 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-178">
            <div data-id="element-179">
              <span className="font-medium text-gray-900" data-id="element-180">Section:</span>{' '}
              {selectedField.section}
            </div>
            <div className="border-l border-gray-200 pl-4" data-id="element-181">
              <span className="font-medium text-gray-900" data-id="element-182">Target:</span>{' '}
              {selectedField.targetFieldApiName || 'None'}
            </div>
          </div>}
      </div>

      {selectedQuestionKey ? <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden" data-id="element-183">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50" data-id="element-184">
            <h3 className="font-semibold text-sm text-gray-900" data-id="element-185">
              Options{' '}
              <span className="font-normal text-gray-500" data-id="element-186">
                ({filteredOptions.length})
              </span>
            </h3>
            <div className="flex gap-2" data-id="element-187">
              <button onClick={() => setIsBulkModalOpen(true)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-188">
                <Zap className="h-3.5 w-3.5 mr-1.5 text-amber-500" data-id="element-189" />
                Quick Add
              </button>
              <button onClick={() => {
            setEditingOption(undefined);
            setIsModalOpen(true);
          }} className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors" data-id="element-190">
                <Plus className="h-3.5 w-3.5 mr-1.5" data-id="element-191" />
                Add Option
              </button>
            </div>
          </div>

          {filteredOptions.length > 0 ? <table className="min-w-full divide-y divide-gray-200" data-id="element-192">
              <thead className="bg-gray-50" data-id="element-193">
                <tr data-id="element-194">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-24" data-id="element-195">
                    Order
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-196">
                    Label
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-197">
                    Value
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-20" data-id="element-198">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 w-24" data-id="element-199">
                    <span className="sr-only" data-id="element-200">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-201">
                {filteredOptions.map((option, index) => <tr key={option.id} className="hover:bg-blue-50/40 transition-colors group" data-id="element-202">
                    <td className="px-6 py-3.5 whitespace-nowrap" data-id="element-203">
                      <div className="flex items-center gap-1.5" data-id="element-204">
                        <span className="text-sm font-medium text-gray-900 w-5 tabular-nums" data-id="element-205">
                          {option.order}
                        </span>
                        <div className="flex flex-col" data-id="element-206">
                          <button onClick={() => onReorder(option.id, 'up')} disabled={index === 0} className="text-gray-300 hover:text-blue-600 disabled:opacity-20 transition-colors p-0.5" data-id="element-207">
                            <ChevronUp className="h-3.5 w-3.5" data-id="element-208" />
                          </button>
                          <button onClick={() => onReorder(option.id, 'down')} disabled={index === filteredOptions.length - 1} className="text-gray-300 hover:text-blue-600 disabled:opacity-20 transition-colors p-0.5" data-id="element-209">
                            <ChevronDown className="h-3.5 w-3.5" data-id="element-210" />
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-211">
                      {option.optionLabel}
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap text-sm text-gray-400 font-mono" data-id="element-212">
                      {option.optionValue}
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap" data-id="element-213">
                      <button onClick={() => onUpdate(option.id, {
                active: !option.active
              })} className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${option.active ? 'bg-green-500' : 'bg-gray-300'}`} aria-label={option.active ? 'Deactivate' : 'Activate'} data-id="element-214">
                        <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${option.active ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-215" />
                      </button>
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap text-right" data-id="element-216">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-217">
                        <button onClick={() => handleEdit(option)} className="text-gray-400 hover:text-blue-600 p-1.5 rounded-lg hover:bg-blue-50 transition-colors" title="Edit" data-id="element-218">
                          <Edit className="h-4 w-4" data-id="element-219" />
                        </button>
                        <button onClick={() => setDeleteId(option.id)} className="text-gray-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Delete" data-id="element-220">
                          <Trash2 className="h-4 w-4" data-id="element-221" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table> : <EmptyState icon={List} title="No options defined" description="Add options for this picklist field to display in the form." action={<button onClick={() => {
        setEditingOption(undefined);
        setIsModalOpen(true);
      }} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 transition-colors" data-id="element-223">
                  <Plus className="h-4 w-4 mr-2" data-id="element-224" />
                  Add Option
                </button>} data-id="element-222" />}
        </div> : <EmptyState icon={List} title="Select a field" description="Choose a picklist field from the dropdown above to manage its options." data-id="element-225" />}

      <PicklistOptionModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} initialData={editingOption} questionKey={selectedQuestionKey} data-id="element-226" />

      {/* Bulk Add Modal */}
      {isBulkModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-id="element-227">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden" data-id="element-228">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-229">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-230">
                Quick Add Options
              </h2>
              <button onClick={() => setIsBulkModalOpen(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Close" data-id="element-231">
                <X className="w-5 h-5" data-id="element-232" />
              </button>
            </div>
            <div className="p-6" data-id="element-233">
              <p className="text-sm text-gray-600 mb-4" data-id="element-234">
                Enter one option per line. Format:{' '}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono" data-id="element-235">
                  Label | Value
                </code>{' '}
                <span className="text-gray-400" data-id="element-236">(Value is optional)</span>
              </p>
              <textarea value={bulkText} onChange={e => setBulkText(e.target.value)} rows={8} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono placeholder:text-gray-400" placeholder={'Single Family Detached | SingleFamily\nCondominium | Condo\nTownhouse'} data-id="element-237" />
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t border-gray-200" data-id="element-238">
              <button onClick={() => setIsBulkModalOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-239">
                Cancel
              </button>
              <button onClick={handleBulkSave} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors" data-id="element-240">
                Add Options
              </button>
            </div>
          </div>
        </div>}

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={() => deleteId && onDelete(deleteId)} title="Delete Option" message="Are you sure you want to delete this option? This action cannot be undone." confirmLabel="Delete Option" data-id="element-241" />
    </div>;
}